"use client";

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUp, User } from "geist-icons";
import CopyButton from "@/components/ui/copy-button";
import { BrainIcon } from "lucide-react";

const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.3,
      delayChildren: 0.2,
    },
  },
};

const lineVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0 },
};

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < breakpoint);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [breakpoint]);

  return isMobile;
}

export function AIBentoGrid() {
  const [isHovered, setIsHovered] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    setHasMounted(true); // Prevent hydration mismatch
  }, []);

  const shouldShowResponse = isMobile || isHovered;
  const shouldShowThinking = !isMobile && !isHovered;

  return (
    <div className="w-full h-full p-4 overflow-hidden">
      <h3 className="text-md sm:text-lg lg:text-2xl font-semibold">
        AI Generated content to help you grow.
      </h3>

      {/* Chat Window */}
      <div
        className="relative group bg-white dark:bg-black rounded-xl shadow-lg w-full h-[283px] md:h-[345px] lg:h-[340px] max-w-md mx-auto mt-3 md:mt-4 border border-[#C5C5C5] dark:border-[#1E1E1E] space-y-4"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Address bar */}
        <div className="flex items-center rounded-xl rounded-b-none bg-[#F0F0F0] dark:bg-[#0A0A0A] p-2 pl-4 gap-8 py-2">
          <div className="flex flex-row gap-2 md:group-hover:blur-xs">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <div className="w-3 h-3 rounded-full bg-yellow-500" />
            <div className="w-3 h-3 rounded-full bg-green-500" />
          </div>
          <div className="relative flex w-full items-center pl-2 truncate justify-between rounded-full py-0.5 transition-all border md:border-[#C5C5C5] md:dark:border-[#1E1E1E] md:group-hover:border-[#FF990A] border-[#FF990A] md:group-hover:dark:border-[#FF990A] dark:border-[#FF990A] md:group-hover:bg-[#FFF4D6] md:group-hover:dark:bg-[#331C00] md:bg-white bg-[#FFF4D6] md:dark:bg-black dark:bg-[#331C00]">
            <p className="flex-1 min-w-0 text-sm truncate text-center">
              mychat.com
            </p>
            <div className="flex-shrink-0 w-9 flex items-center justify-center">
              <CopyButton text="mychat.com" />
            </div>
          </div>
        </div>

        {/* User Static Message */}
        <div className="flex flex-row px-10 items-center justify-end gap-5">
          <div className="w-1/2 h-6 rounded-full bg-[#C9C9C9] dark:bg-[#5C5C5C] md:group-hover:blur-xs" />
          <div className="w-10 h-10 p-[9px] rounded-full bg-[#FFF4D6] dark:bg-[#331C00] md:bg-[#C9C9C9] md:dark:bg-[#5C5C5C] border border-[#FF990A] dark:border-[#FF990A] md:border-[#C9C9C9] md:dark:border-[#5C5C5C] transition-all md:group-hover:border-[#FF990A] hover:border-[#FF990A] dark:hover:border-[#FF990A] md:group-hover:bg-[#FFF4D6] md:group-hover:dark:border-[#FF990A] group-hover:dark:bg-[#331C00]">
            <User className="w-4 h-4 text-[#B2B2B2] dark:text-[#787878]" />
          </div>
        </div>

        {/* Assistant AI Response */}
        <div className="flex flex-row px-10 items-start justify-start gap-5">
          <div className="w-10 h-10 p-[7px] rounded-full bg-[#FFF4D6] dark:bg-[#331C00] md:bg-[#C9C9C9] md:dark:bg-[#5C5C5C] border border-[#FF990A] dark:border-[#FF990A] md:border-[#C9C9C9] md:dark:border-[#5C5C5C] transition-all md:group-hover:border-[#FF990A] hover:border-[#FF990A] dark:hover:border-[#FF990A] md:group-hover:bg-[#FFF4D6] md:group-hover:dark:border-[#FF990A] group-hover:dark:bg-[#331C00]">
            <BrainIcon className="text-[#B2B2B2] dark:text-[#787878]" />
          </div>

          {/* Content Area */}
          {hasMounted && (
            <>
              {isMobile ? (
                <div className="space-y-2 flex flex-col w-1/2">
                  {[1, 2, 3].map((_, i) => (
                    <div
                      key={i}
                      className="h-6 rounded-md border bg-[#FFF4D6] dark:bg-[#331C00] border-[#FF990A] dark:border-[#FF990A]"
                    />
                  ))}
                </div>
              ) : (
                <AnimatePresence mode="wait">
                  {shouldShowResponse && (
                    <motion.div
                      key="response"
                      className="space-y-2 flex flex-col w-1/2"
                      initial="hidden"
                      animate="visible"
                      exit="hidden"
                      variants={containerVariants}
                    >
                      {[1, 2, 3].map((_, i) => (
                        <motion.div
                          key={i}
                          variants={lineVariants}
                          className="h-6 rounded-md border bg-[#FFF4D6] dark:bg-[#331C00] border-[#FF990A] dark:border-[#FF990A]"
                        />
                      ))}
                    </motion.div>
                  )}

                  {shouldShowThinking && (
                    <motion.div
                      key="thinking"
                      className="w-4 h-4 mt-3 rounded-full bg-[#C9C9C9] dark:bg-[#5C5C5C]"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    />
                  )}
                </AnimatePresence>
              )}
            </>
          )}
        </div>

        {/* Fixed Form Input */}
        <div className="absolute flex flex-row inset-0 top-3/4 md:top-1/2 items-center justify-center gap-2 md:group-hover:blur-xs">
          <div className="w-1/2 h-6 rounded-full bg-[#C9C9C9] dark:bg-[#5C5C5C] flex items-center justify-end pr-2">
            <ArrowUp className="w-5 h-5 scale-80 text-[#B2B2B2] dark:text-[#787878]" />
          </div>
        </div>

        {/* Shadow */}
        <div className="absolute -bottom-5 -left-2 w-[110%] h-14 md:h-20 lg:h-30 bg-gradient-to-t from-[#FAFAFA] via-[#FAFAFA] to-transparent dark:from-black dark:via-black pointer-events-none z-[40]" />
      </div>
    </div>
  );
}
